import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Button, message } from 'antd';
import { DownOutlined, DownloadOutlined, RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ChangeLogButton from '../changeLog/ChangeLogButton';
import ExportButton from '../../../components/common/ExportButton';
import ExportPanel, { ExportColumnConfig } from '../../../components/common/ExportPanel';
import RiskAssessmentExportPanel from './total/RiskAssessmentExportPanel';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from '@syncfusion/ej2-react-grids';
import { RootState, AppDispatch } from '../../../store';
import { 
  getRiskAssessmentProcesses, 
  parseAndGroupRiskAssessments,
  Cycle as RiskAssessmentCycle,
  RiskAssessment
} from '../../../services/riskAssessmentService';
import {
  setRiskAssessmentCycles,
  clearRiskAssessmentData,
} from '../../../store/slices/riskAssessmentSlice';

const { Title } = Typography;

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;


const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
`;

const ContentWrapper = styled.div``;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding-top: 200px;
  z-index: 1000;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PageEmptyStateWrapper = styled.div`
  text-align: center;
  padding: 100px 20px;
  min-height: 400px;
`;

const PageEmptyStateText = styled.div`
  color: #1F2937;
  margin-bottom: 24px;
  font-size: 18px;
  font-weight: 500;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CycleSection = styled.div<{ $bgColor: string }>`
  margin-bottom: 24px;
  background: ${props => props.$bgColor};
  padding: 16px 2px 2px 2px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const CycleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  margin-left: 16px;
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const CollapseIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  transition: transform 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CycleTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #1F2937;
  flex: 1;
`;

const GridWrapper = styled.div`
  background: white;
  border-radius: 6px;
  overflow: hidden;

  .e-grid {
    border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    padding: 6px 8px;
    font-size: 12px;
    color: #1F2937;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  /* Prevent dark hover/active state */
  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const ProcessChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;

const ProcessOwnerChip = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #F8FAFC;
  color: #1F2937;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.3s;
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Unranked':
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

const ClickableNumber = styled.span`
  color: #1F2937;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: #2563EB;
    text-decoration: underline;
  }
`;

const SubTotalRow = styled.div`
  display: grid;
  grid-template-columns: auto 200px 160px 160px 140px 140px;
  align-items: center;
  background: white;
  border-radius: 0 0 6px 6px;
  border-top: 2px solid #E5E7EB;
  margin-top: 6px;
  
  > div {
    padding: 16px 8px;
    height: 100%;
    border-right: 1px solid #E5E7EB;
    
    &:last-child {
      border-right: none;
    }
  }
`;

const SubTotalLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
`;

const SubTotalValue = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
  text-align: center;
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SummaryLabel = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
`;

const SummaryValue = styled.span`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #2563EB;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

// ==================== COMPONENT ====================

interface CycleItemProps {
  cycle: RiskAssessmentCycle;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onNavigateToDetail: (riskAssessmentId: string, defaultTab?: string) => void;
}

const CycleItem: React.FC<CycleItemProps> = ({
  cycle,
  isCollapsed,
  onToggleCollapse,
  onNavigateToDetail,
}) => {
  const hasRiskAssessments = cycle.riskAssessments.length > 0;

  // Calculate Sub Total
  const calculateSubTotal = () => {
    if (!hasRiskAssessments) {
      return { totalObjectives: 0, totalFraudRisks: 0 };
    }

    const totalObjectives = cycle.riskAssessments.reduce(
      (sum, ra) => sum + ra.objectivesCount,
      0
    );
    const totalFraudRisks = cycle.riskAssessments.reduce(
      (sum, ra) => sum + ra.fraudRisksCount,
      0
    );

    return { totalObjectives, totalFraudRisks };
  };

  // Column templates
  const processTemplate = (props: RiskAssessment) => {
    return (
      <ProcessChip onClick={() => onNavigateToDetail(props.id, 'tab1')}>
        {props.processCode} {props.processName}
      </ProcessChip>
    );
  };

  const processOwnerTemplate = (props: RiskAssessment) => {
    // Show missing owner alert if processOwner is empty
    if (!props.processOwnerName || props.processOwnerName.trim() === '') {
      return null;
    }

    return (
      <ProcessOwnerChip>
        {props.processOwnerName}
      </ProcessOwnerChip>
    );
  };

  const riskRankingTemplate = (props: RiskAssessment) => {
    return (
      <RiskChip $risk={props.riskRanking} onClick={() => onNavigateToDetail(props.id, 'tab1')}>
        {props.riskRanking}
      </RiskChip>
    );
  };

  const fraudRiskRankTemplate = (props: RiskAssessment) => {
    return (
      <RiskChip $risk={props.fraudRiskRank} onClick={() => onNavigateToDetail(props.id, 'tab3')}>
        {props.fraudRiskRank}
      </RiskChip>
    );
  };

  const objectivesTemplate = (props: RiskAssessment) => {
    return (
      <ClickableNumber onClick={() => onNavigateToDetail(props.id, 'tab2')}>
        {props.objectivesCount}
      </ClickableNumber>
    );
  };

  const fraudRisksTemplate = (props: RiskAssessment) => {
    return (
      <ClickableNumber onClick={() => onNavigateToDetail(props.id, 'tab3')}>
        {props.fraudRisksCount}
      </ClickableNumber>
    );
  };

  const subTotal = calculateSubTotal();

  return (
    <CycleSection $bgColor={cycle.bgColor}>
      <CycleHeader onClick={onToggleCollapse}>
        <CollapseIcon>
          {isCollapsed ? <RightOutlined /> : <DownOutlined />}
        </CollapseIcon>
        <CycleTitle>
          {cycle.cycleCode} {cycle.cycleName}
        </CycleTitle>
      </CycleHeader>

      {!isCollapsed && hasRiskAssessments && (
        <>
          <GridWrapper>
            <GridComponent
              key={`grid-${cycle.id}-${cycle.riskAssessments.length}`}
              dataSource={cycle.riskAssessments}
              allowPaging={false}
              enableHover={true}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field='processCode'
                  headerText='Processes'
                  width='auto'
                  textAlign='Left'
                  template={processTemplate}
                />
                <ColumnDirective
                  field='processOwner'
                  headerText='Process Owner'
                  width='200'
                  textAlign='Left'
                  template={processOwnerTemplate}
                />
                <ColumnDirective
                  field='riskRanking'
                  headerText='Risk Rankings'
                  width='160'
                  textAlign='Center'
                  template={riskRankingTemplate}
                />
                <ColumnDirective
                  field='fraudRiskRank'
                  headerText='Fraud Risk Rankings'
                  width='160'
                  textAlign='Center'
                  template={fraudRiskRankTemplate}
                />
                <ColumnDirective
                  field='objectivesCount'
                  headerText='Objectives'
                  width='140'
                  textAlign='Center'
                  template={objectivesTemplate}
                />
                <ColumnDirective
                  field='fraudRisksCount'
                  headerText='Fraud Risks'
                  width='140'
                  textAlign='Center'
                  template={fraudRisksTemplate}
                />
              </ColumnsDirective>
              <Inject services={[Page]} />
            </GridComponent>
          </GridWrapper>

          {/* Sub Total Row */}
          <SubTotalRow>
            <SubTotalLabel>Sub Total</SubTotalLabel>
            <div />
            <div />
            <div />
            <SubTotalValue>{subTotal.totalObjectives}</SubTotalValue>
            <SubTotalValue>{subTotal.totalFraudRisks}</SubTotalValue>
          </SubTotalRow>
        </>
      )}
    </CycleSection>
  );
};

const RiskAssessmentPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [collapsedCycles, setCollapsedCycles] = useState<Set<string>>(new Set());
  const [isLoadingCycles, setIsLoadingCycles] = useState(false);
  const [exportPanelOpen, setExportPanelOpen] = useState(false);
  const [consolidatedExportOpen, setConsolidatedExportOpen] = useState(false);
  
  // Get current annual report from Redux
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  
  // Get risk assessment data from Redux
  const { cycles, lastFetchedReportId } = useSelector((state: RootState) => state.riskAssessment);

  // Tracks which report was last fetched to detect fiscal year changes
  const lastFetchedForReport = useRef<string | null>(null);

  // Export columns configuration
  const exportColumns: ExportColumnConfig[] = useMemo(() => [
    { key: 'cycle', title: 'Cycle', width: 20 },
    { key: 'process', title: 'Process', width: 25 },
    { key: 'processOwner', title: 'Process Owner', width: 18 },
    { key: 'riskRanking', title: 'Risk Ranking', width: 12 },
    { key: 'fraudRiskRank', title: 'Fraud Risk Rank', width: 12 },
    { key: 'objectivesCount', title: 'Objectives', width: 10 },
    { key: 'fraudRisksCount', title: 'Fraud Risks', width: 10 },
  ], []);

  // Prepare export data - flatten cycles and risk assessments
  const exportData = useMemo(() => {
    const data: Record<string, unknown>[] = [];
    
    cycles.forEach(cycle => {
      cycle.riskAssessments.forEach(ra => {
        data.push({
          cycle: `${cycle.cycleCode} ${cycle.cycleName}`,
          process: `${ra.processCode} ${ra.processName}`,
          processOwner: ra.processOwnerName,
          riskRanking: ra.riskRanking,
          fraudRiskRank: ra.fraudRiskRank,
          objectivesCount: ra.objectivesCount,
          fraudRisksCount: ra.fraudRisksCount,
        });
      });
    });
    
    return data;
  }, [cycles]);

  // Fetch risk assessment processes when selectedReport changes (Offline-first approach with smart caching)
  useEffect(() => {
    const fetchRiskAssessments = async () => {
      if (!selectedReport?.annualReportId) {
        dispatch(clearRiskAssessmentData());
        lastFetchedForReport.current = null;
        return;
      }

      const annualReportId = selectedReport.annualReportId;
      const alreadyFetchedForThisReport = lastFetchedForReport.current === annualReportId;
      const hasCachedData = lastFetchedReportId === annualReportId;

      if (alreadyFetchedForThisReport && hasCachedData) {
        return;
      }

      lastFetchedForReport.current = annualReportId;
      setIsLoadingCycles(true);

      try {
        const response = await getRiskAssessmentProcesses(annualReportId);
        const parsedCycles = parseAndGroupRiskAssessments(response.data);
        
        dispatch(setRiskAssessmentCycles({
          cycles: parsedCycles,
          reportId: annualReportId,
        }));
      } catch (error: any) {
        message.error(error?.message || 'Failed to load risk assessment data');
        lastFetchedForReport.current = null;
      } finally {
        setIsLoadingCycles(false);
      }
    };

    fetchRiskAssessments();
  }, [selectedReport?.annualReportId, lastFetchedReportId, dispatch]);

  const toggleCycleCollapse = (cycleId: string) => {
    setCollapsedCycles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cycleId)) {
        newSet.delete(cycleId);
      } else {
        newSet.add(cycleId);
      }
      return newSet;
    });
  };

  const handleNavigateToDetail = (riskAssessmentId: string, defaultTab?: string) => {
    const tab = defaultTab || 'tab1';
    navigate(`/risk-assessment/${riskAssessmentId}/${tab}`);
  };

  // Calculate overall totals across all cycles
  const overallTotals = useMemo(() => {
    let totalObjectives = 0;
    let totalFraudRisks = 0;

    cycles.forEach(cycle => {
      cycle.riskAssessments.forEach(ra => {
        totalObjectives += ra.objectivesCount;
        totalFraudRisks += ra.fraudRisksCount;
      });
    });

    return { totalObjectives, totalFraudRisks };
  }, [cycles]);

  // Render loading state (prioritize over empty state on initial load)
  if (isLoadingCycles) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>ICFR Risk Assessment</PageTitle>
        </PageHeader>
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingOverlay>
            <LoadingSpinner>
              <div className="spinner" />
              <div className="text">Loading risk assessment data...</div>
            </LoadingSpinner>
          </LoadingOverlay>
        </div>
      </PageContainer>
    );
  }

  // Render empty state for entire page when no cycles
  // Only show if we've attempted to fetch (not initial load)
  if (cycles.length === 0 && lastFetchedReportId !== null) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle level={2}>ICFR Risk Assessment</PageTitle>
        </PageHeader>
        <PageEmptyStateWrapper>
          <PageEmptyStateText>No risk assessment data found for this annual report.</PageEmptyStateText>
        </PageEmptyStateWrapper>
      </PageContainer>
    );
  }

  // Show nothing while waiting for initial load (before first fetch)
  if (cycles.length === 0 && lastFetchedReportId === null) {
    return null;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderRow>
          <PageTitle level={2}>ICFR Risk Assessment</PageTitle>
          <HeaderActions>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => setConsolidatedExportOpen(true)}
              style={{ height: 40 }}
            >
              Export Consolidated Risks
            </Button>
            <ExportButton onClick={() => setExportPanelOpen(true)} />
            {selectedReport && isAdmin && (
              <ChangeLogButton
                actionType="riskAssessment"
                annualReportId={selectedReport.annualReportId}
              />
            )}
          </HeaderActions>
        </PageHeaderRow>
        <SummaryRow>
          <SummaryItem>
            <SummaryLabel>Total Objectives:</SummaryLabel>
            <SummaryValue onClick={() => navigate('/risk-assessment-total/objectives')}>
              {overallTotals.totalObjectives}
            </SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total Fraud Risks:</SummaryLabel>
            <SummaryValue onClick={() => navigate('/risk-assessment-total/fraud-risks')}>
              {overallTotals.totalFraudRisks}
            </SummaryValue>
          </SummaryItem>
        </SummaryRow>
      </PageHeader>

      <ContentWrapper>
        {cycles.map(cycle => (
          <CycleItem
            key={cycle.id}
            cycle={cycle}
            isCollapsed={collapsedCycles.has(cycle.id)}
            onToggleCollapse={() => toggleCycleCollapse(cycle.id)}
            onNavigateToDetail={handleNavigateToDetail}
          />
        ))}
      </ContentWrapper>

      {/* Export Panel */}
      <ExportPanel
        open={exportPanelOpen}
        onClose={() => setExportPanelOpen(false)}
        data={exportData}
        columns={exportColumns}
        fileName="RiskAssessment"
        title="ICFR Risk Assessment"
        metadataRows={[
          { label: 'Total Objectives', value: String(overallTotals.totalObjectives) },
          { label: 'Total Fraud Risks', value: String(overallTotals.totalFraudRisks) },
        ]}
      />

      {/* Consolidated Export Panel */}
      <RiskAssessmentExportPanel
        open={consolidatedExportOpen}
        onClose={() => setConsolidatedExportOpen(false)}
      />
    </PageContainer>
  );
};

export default RiskAssessmentPage;
